import { lookup } from "@repo/vihangam-yog-shared";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "./auth/AuthContext";
import { CHAPTER } from "./chapter";
import { Mark } from "./components/Mark";
import { Shell, type NavId } from "./components/Shell";
import { AppInterest } from "./pages/AppInterest";
import { AwaitingApproval } from "./pages/AwaitingApproval";
import { Emails } from "./pages/Emails";
import { EventRegistrations } from "./pages/EventRegistrations";
import { Feedback } from "./pages/Feedback";
import { Inquiries } from "./pages/Inquiries";
import { MemberCard } from "./pages/MemberCard";
import { Members } from "./pages/Members";
import { Overview } from "./pages/Overview";
import { PanelQuestions } from "./pages/PanelQuestions";
import { Roles } from "./pages/Roles";
import { Settings } from "./pages/Settings";
import { SignIn } from "./pages/SignIn";
import { Reception } from "./pages/reception/Reception";

/** Only the pages this chapter runs; the same build serves every chapter. */
function chapterNav(ids: NavId[]): NavId[] {
  return ids.filter((id) => CHAPTER.features.includes(id));
}

// Team members (and legacy coordinator accounts) see no people data
// (organisers only since #117): just Feedback where the chapter runs it,
// and their account.
const TEAM_NAV: NavId[] = chapterNav(["feedback", "settings"]);
const SUPERADMIN_NAV: NavId[] = chapterNav([
  "overview",
  "events",
  "reception",
  "panel-questions",
  "members",
  "emails",
  "inquiries",
  "app-interest",
  "roles",
  "feedback",
  "settings",
]);

/** How long the loading screen waits before suggesting the connection. */
const SLOW_LOAD_MS = 8000;

/**
 * Tab ↔ URL path mapping. Each sidebar tab gets a stable URL slug so
 * the active tab can be shared (<adminHost>/events points straight at
 * Events). Home keeps both "/" and "/overview" so the bare root URL also
 * lands somewhere sensible. Any other path, including the retired
 * /coordination pages, lands on Home.
 */
const PATH_TO_NAV = {
  "/": "overview",
  "/overview": "overview",
  "/events": "events",
  "/reception": "reception",
  "/panel-questions": "panel-questions",
  "/members": "members",
  "/emails": "emails",
  "/inquiries": "inquiries",
  "/app-interest": "app-interest",
  "/roles": "roles",
  "/feedback": "feedback",
  "/settings": "settings",
} satisfies Record<string, NavId>;
const NAV_TO_PATH = {
  overview: "/overview",
  events: "/events",
  reception: "/reception",
  "panel-questions": "/panel-questions",
  members: "/members",
  emails: "/emails",
  inquiries: "/inquiries",
  "app-interest": "/app-interest",
  roles: "/roles",
  feedback: "/feedback",
  settings: "/settings",
} satisfies Record<NavId, string>;

/** Strip a single trailing slash and lowercase so /Events and /events/
 *  both resolve, but otherwise read the path as-is. */
function normalisePath(pathname: string): string {
  return (
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname
  ).toLowerCase();
}

/** The tab a path names, before any chapter or role check. Reception owns
 *  everything under its prefix (/reception/<eventSlug>, ?ref=… for a
 *  scanned pass). Undefined for a path no tab owns. */
function tabOwningPath(pathname: string): NavId | undefined {
  const trimmed = normalisePath(pathname);
  return trimmed.startsWith("/reception")
    ? "reception"
    : lookup(PATH_TO_NAV, trimmed);
}

function navFromPath(pathname: string): NavId {
  const tab = tabOwningPath(pathname) ?? "overview";
  // A tab this chapter does not run (/members on Singapore) lands on
  // overview rather than rendering a page the chapter does not have.
  return CHAPTER.features.includes(tab) ? tab : "overview";
}

/**
 * Shown while sign-in and access are checked. On venue wifi that can
 * take a while (the access lookup retries), so after a few seconds it
 * says what to check rather than leaving a silent screen.
 */
function LoadingScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div
      role="status"
      className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <Mark size={44} />
      <p className="text-[16px] text-[color:var(--color-ink)]">
        Loading the team app…
      </p>
      {slow ? (
        <p className="max-w-sm text-[16px] text-[color:var(--color-ink-soft)]">
          Still loading. Check your internet connection.
        </p>
      ) : null}
    </div>
  );
}

export function App() {
  const { isAuthenticated, isLoaded, isResolved, role, roleError } = useAuth();

  // Pages the current role can open. Members and people with no role
  // never reach the Shell, so they get none.
  const allowedNav = useMemo<NavId[]>(
    () =>
      role === "superadmin"
        ? SUPERADMIN_NAV
        : role === "volunteer" || role === "coordinator"
          ? TEAM_NAV
          : [],
    [role],
  );
  const [active, setActive] = useState<NavId>(() =>
    navFromPath(window.location.pathname),
  );

  // Keep the URL in sync with the active tab and respond to back/
  // forward navigation. pushState only when the URL would actually
  // change to avoid duplicate history entries.
  const navigate = useCallback(
    (next: NavId) => {
      // A page this person can't open, or this chapter doesn't run, is
      // never a destination, whoever asks for it.
      if (!allowedNav.includes(next)) {
        return;
      }
      setActive(next);
      if (next === "reception") {
        // The Reception page owns the deeper URL (event slug, ?ref=);
        // only land under /reception when arriving from another tab.
        if (!window.location.pathname.toLowerCase().startsWith("/reception")) {
          window.history.pushState({}, "", "/reception");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
        return;
      }
      const targetPath = NAV_TO_PATH[next];
      if (window.location.pathname !== targetPath) {
        window.history.pushState({}, "", targetPath);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    },
    [allowedNav],
  );

  useEffect(() => {
    const onPop = () => setActive(navFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // If `active` falls outside the allowed set (role downgrade,
  // unknown URL slug, future pages) snap back to the first allowed
  // entry so a blank page never renders. Also keeps the URL in
  // sync after the snap, and tidies an address that landed on a tab it
  // doesn't name (the retired /coordination pages, /members on
  // Singapore) so the bar shows the page actually on screen.
  useEffect(() => {
    if (allowedNav.length === 0) {
      return;
    }
    if (!allowedNav.includes(active)) {
      const fallback = allowedNav[0] ?? "overview";
      setActive(fallback);
      const targetPath = NAV_TO_PATH[fallback];
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({}, "", targetPath);
      }
      return;
    }
    if (tabOwningPath(window.location.pathname) !== active) {
      window.history.replaceState({}, "", NAV_TO_PATH[active]);
    }
  }, [active, allowedNav]);

  if (!isLoaded || (isAuthenticated && !isResolved)) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <SignIn />;
  }

  // Signed in, role resolved. Route by access level.
  // A failed lookup is NOT "no access" — never show someone the
  // no-access screen because a request timed out on venue wifi.
  if (roleError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div role="alert" className="max-w-md text-center">
          <h1 className="page-title">We can&apos;t connect right now</h1>
          <p className="mt-3 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
            This is usually the internet connection. Check your wifi or mobile
            data, then try again. Your access hasn&apos;t changed.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex min-h-11 cursor-pointer items-center rounded-full bg-[color:var(--color-ink)] px-6 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-85"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (role === null) {
    return <AwaitingApproval />;
  }
  if (role === "member") {
    // A membership card only exists where the chapter runs memberships.
    // Elsewhere a member role opens nothing, so it reads as no access.
    return CHAPTER.features.includes("members") ? (
      <MemberCard />
    ) : (
      <AwaitingApproval />
    );
  }

  // Before the snap-back effect runs, never render a page outside the
  // allowed set, not even for one frame.
  const shown: NavId = allowedNav.includes(active)
    ? active
    : (allowedNav[0] ?? "overview");

  // Team members (legacy coordinators included) and organisers → the
  // team app.
  return (
    <Shell active={shown} onNavigate={navigate} allowedNav={allowedNav}>
      {shown === "overview" && <Overview onNavigate={navigate} />}
      {shown === "events" && <EventRegistrations />}
      {shown === "reception" && <Reception />}
      {shown === "panel-questions" && <PanelQuestions />}
      {shown === "members" && <Members />}
      {/* Every send on this page is organiser-only on the server. */}
      {shown === "emails" && role === "superadmin" && <Emails />}
      {shown === "inquiries" && <Inquiries />}
      {shown === "app-interest" && role === "superadmin" && <AppInterest />}
      {shown === "roles" && role === "superadmin" && <Roles />}
      {shown === "feedback" && <Feedback />}
      {shown === "settings" && <Settings onNavigate={navigate} />}
    </Shell>
  );
}
